import type { RevieweeDeliveryBadge, ReviewSharingStatus, ReviewStatus, ReviewType } from "#shared/reviewsContract.ts";
import { formatUtcMonthYear, formatUtcShortDate, parseUtcDate } from "@packages/core/time/dateFormatting.ts";
import type { BadgeIntent } from "@packages/core-client/components/Badge.tsx";

export function formatShortDate(value: string, locale: string): string {
    return formatUtcShortDate(value, locale) ?? value;
}

export function formatScore(score: number | undefined, scoreScale: number | undefined): string | undefined {
    if (score === undefined) {
        return undefined;
    }

    if (scoreScale === undefined) {
        return String(score);
    }

    return `${score} / ${scoreScale}`;
}

export function formatEvaluationPeriod(periodStart: string | undefined, periodEnd: string | undefined, locale: string): string | undefined {
    const start = parseUtcDate(periodStart);
    const end = parseUtcDate(periodEnd);

    if (!start && !end) {
        return undefined;
    }

    if (!start) {
        return formatUtcMonthYear(periodEnd, locale);
    }

    if (!end) {
        return formatUtcMonthYear(periodStart, locale);
    }

    const startYear = start.getUTCFullYear();

    if (startYear === end.getUTCFullYear()) {
        const monthOnly = new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" });

        return `${monthOnly.format(start)} - ${monthOnly.format(end)} ${startYear}`;
    }

    return `${formatUtcMonthYear(periodStart, locale)} - ${formatUtcMonthYear(periodEnd, locale)}`;
}

export const SHARING_BADGE_INTENT: Record<ReviewSharingStatus, BadgeIntent> = {
    NotShared: "muted",
    Shared: "success",
    Acknowledged: "success"
};

export const REVIEW_TYPE_INTENT: Record<ReviewType, BadgeIntent> = {
    SelfReview: "muted",
    ManagerReview: "warning",
    PeerReview: "muted",
    UpwardReview: "muted"
};

export const REVIEW_STATUS_INTENT: Record<ReviewStatus, BadgeIntent> = {
    NotStarted: "muted",
    InProgress: "primary",
    Submitted: "success"
};

export const DELIVERY_BADGE_INTENT: Record<RevieweeDeliveryBadge, BadgeIntent> = {
    InProgress: "primary",
    Shared: "primary",
    Acknowledged: "success"
};

export function formatDueDate(value: string | undefined, locale: string): string | undefined {
    if (!value) {
        return undefined;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return undefined;
    }

    return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", timeZone: "UTC" }).format(date);
}
